import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import DataTable from '../components/DataTable';

const ListarGuiasdeAprendizaje = ({ refresh }) => {
  const [guias, setGuias] = useState([]);
  const [loading, setLoading] = useState(false);

  const { codPrograma } = useParams();
  const navigate = useNavigate();

  const generarYDescargarGuia = async (idGuia, codProgramaValue, numeroGuia) => {
    try {
      const response = await axios.get(`/api/guia-aprendizaje/generar-docx/${idGuia}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `GuiaAprendizaje_${codProgramaValue}_N${numeroGuia}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error al generar la guía:', error.message);
      Swal.fire('Error', 'No se pudo generar la guía.', 'error');
    }
  };

  const deleteGuia = useCallback(
    async (idGuia) => {
      try {
        const confirm = await Swal.fire({
          title: '¿Está seguro?',
          text: '¡Esta acción no se puede deshacer!',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Sí, eliminar',
          cancelButtonText: 'Cancelar',
        });

        if (!confirm.isConfirmed) return;

        const { data } = await axios.delete(`/api/programas/guias/delete/${idGuia}`);

        // ✅ actualización optimista (sin recargar, estilo mrgestor)
        setGuias((prev) => prev.filter((g) => g._id !== idGuia));

        Swal.fire({
          icon: 'success',
          title: data.message || 'Guía eliminada',
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (error) {
        console.error('Error al eliminar la guía:', error.message);
        Swal.fire('Error', 'No se pudo eliminar la guía.', 'error');
      }
    },
    []
  );

  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`/api/programas/guias/listidprograma/${codPrograma}`);

      if (response.data && response.data.ok && Array.isArray(response.data.guias)) {
        const guiasOrdenadas = response.data.guias.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );

        const total = guiasOrdenadas.length;

        const guiasConFunciones = guiasOrdenadas.map((guia, index) => {
          const numeroGuia = total - index;

          return {
            ...guia,
            numeroGuia,

            // ✅ iconos horizontales + estilo mrgestor
            funciones: (
              <div className="d-flex align-items-center justify-content-center gap-2 flex-nowrap">


                {/* Descargar */}
                <button
                  className="btn btn-dark btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{ width: 34, height: 34 }}
                  onClick={() => generarYDescargarGuia(guia._id, guia.codPrograma, numeroGuia)}
                  title={`Descargar Guía N° ${numeroGuia}`}
                  type="button"
                >
                  <i className="fa-solid fa-file-word"></i>
                </button>

                {/* Editar */}
                <button
                  className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{ width: 34, height: 34 }}
                  onClick={() => navigate(`/institucional/guias/editar/${guia._id}`)}
                  title="Editar guía"
                  type="button"
                >
                  <i className="fa-solid fa-pen-to-square"></i>
                </button>

                {/* Eliminar */}
                <button
                  className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                  style={{ width: 34, height: 34 }}
                  onClick={() => deleteGuia(guia._id)}
                  title="Eliminar guía"
                  type="button"
                >
                  <i className="fa-solid fa-trash"></i>
                </button>
              </div>
            ),
          };
        });

        setGuias(guiasConFunciones);
      } else {
        setGuias([]);
      }
    } catch (error) {
      console.error('Error al cargar las guías:', error.message);
      setGuias([]);
     
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [codPrograma, navigate, deleteGuia]);

  useEffect(() => {
    if (codPrograma) getData();
  }, [codPrograma, getData, refresh]);

  const columns = useMemo(
    () => [
      { Header: 'N° Guía', accessor: 'numeroGuia' },
      { Header: 'Programa', accessor: 'nombrePrograma' },
      { Header: 'Proyecto Formativo', accessor: 'nombreProyectoFormativo' },
      { Header: 'Fase', accessor: 'faseProyecto' },
      { Header: 'Acciones', accessor: 'funciones' },
    ],
    []
  );

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">Guías de aprendizaje</h5>
                <small className="text-secondary">
                  Gestión de guías por programa: edición, descarga y eliminación.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => navigate('/institucional/programas')}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>

                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => getData()}
                  title="Actualizar"
                  disabled={loading}
                >
                  <i className={`fa-solid ${loading ? 'fa-spinner fa-spin' : 'fa-rotate'} me-2`}></i>
                  Actualizar
                </button>

                <button
                  className="btn btn-dark"
                  type="button"
                  onClick={() => navigate(`/institucional/guias/nuevo/${codPrograma}`)}
                  disabled={loading || !codPrograma}
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Nuevo
                </button>

              </div>
            </div>
          </div>

          {/* Body */}
          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div className="spinner-border" role="status" aria-hidden="true"></div>
                <div className="mt-3">Cargando guías…</div>
              </div>
            ) : Array.isArray(guias) && guias.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={guias} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                 <div className="display-6 mb-2">📄</div>
                <div className="fw-semibold">No hay guías disponibles</div>
                <div className="small">No se encontraron guías para este programa.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarGuiasdeAprendizaje;
